import { Dimension } from '../data/questions'

export interface DimensionScore {
  dimension: Dimension
  score: number
  maxScore: number
  percentage: number
}

export interface AssessmentResult {
  overallScore: number
  maturityLevel: string
  levelDescription: string
  dimensionScores: DimensionScore[]
  recommendations: string[]
}

export const calculateResults = (
  answers: Record<string, number>,
): AssessmentResult => {
  // Group scores by dimension
  const dimensionTotals: Record<string, { sum: number; count: number }> = {}

  // This requires importing questions, but to keep utils pure we'll rely on the keys passed or hardcode dimensions
  // For simplicity in this architecture, we will infer from the question ID prefixes or pass the questions structure
  // Let's use a mapping based on the known dimensions

  const dimensions: Dimension[] = [
    'Estratégia',
    'Portfólio',
    'Processos',
    'Cultura',
    'Proteção',
  ]

  // Initialize
  dimensions.forEach((d) => {
    dimensionTotals[d] = { sum: 0, count: 0 }
  })

  // Map question IDs to dimensions (simple logic based on ID prefix)
  Object.entries(answers).forEach(([questionId, value]) => {
    let dim: Dimension | undefined
    if (questionId.startsWith('strat')) dim = 'Estratégia'
    else if (questionId.startsWith('port')) dim = 'Portfólio'
    else if (questionId.startsWith('proc')) dim = 'Processos'
    else if (questionId.startsWith('cult')) dim = 'Cultura'
    else if (questionId.startsWith('prot')) dim = 'Proteção'

    if (dim) {
      dimensionTotals[dim].sum += value
      dimensionTotals[dim].count += 1
    }
  })

  // Calculate dimension scores
  const dimensionScores: DimensionScore[] = dimensions.map((dim) => {
    const { sum, count } = dimensionTotals[dim]
    const avg = count > 0 ? sum / count : 0
    return {
      dimension: dim,
      score: Number(avg.toFixed(1)),
      maxScore: 5,
      percentage: (avg / 5) * 100,
    }
  })

  // Calculate overall score
  const totalSum = dimensionScores.reduce((acc, curr) => acc + curr.score, 0)
  const overallScore = Number((totalSum / dimensions.length).toFixed(1))

  // Determine Level
  let maturityLevel = ''
  let levelDescription = ''

  if (overallScore <= 1.5) {
    maturityLevel = 'Inicial'
    levelDescription =
      'Sua gestão de PI é ad-hoc e reativa. Processos são inexistentes ou desorganizados, e a PI não é vista como estratégica.'
  } else if (overallScore <= 2.5) {
    maturityLevel = 'Gerenciado'
    levelDescription =
      'Existem processos básicos e alguma conscientização, mas a gestão ainda é fragmentada e reativa a problemas.'
  } else if (overallScore <= 3.5) {
    maturityLevel = 'Definido'
    levelDescription =
      'Processos de PI são padronizados e documentados. A empresa entende o valor da PI, mas a integração com o negócio pode melhorar.'
  } else if (overallScore <= 4.5) {
    maturityLevel = 'Quantitativo'
    levelDescription =
      'A gestão de PI é medida e controlada por métricas. Há forte alinhamento com a estratégia de negócios e uso de dados para decisão.'
  } else {
    maturityLevel = 'Otimizado'
    levelDescription =
      'Nível de excelência. A PI é um motor de inovação e receita, com melhoria contínua e processos integrados globalmente.'
  }

  // Generate Recommendations based on lowest scores
  const sortedDimensions = [...dimensionScores].sort(
    (a, b) => a.score - b.score,
  )
  const lowestDimensions = sortedDimensions.slice(0, 3)

  const recommendations = lowestDimensions.map((d) => {
    switch (d.dimension) {
      case 'Estratégia':
        return 'Desenvolva um plano estratégico de PI alinhado aos objetivos comerciais da empresa.'
      case 'Portfólio':
        return 'Implemente um sistema centralizado para gestão e revisão periódica dos ativos de PI.'
      case 'Processos':
        return 'Formalize o processo de captura de invenções e crie um comitê de PI para tomadas de decisão.'
      case 'Cultura':
        return 'Invista em treinamentos de conscientização sobre PI para equipes de P&D e liderança.'
      case 'Proteção':
        return 'Estabeleça uma rotina de monitoramento de concorrentes e análise de liberdade de operação (FTO).'
      default:
        return 'Revise seus processos internos de PI.'
    }
  })

  return {
    overallScore,
    maturityLevel,
    levelDescription,
    dimensionScores,
    recommendations,
  }
}
